<template>
	<div class="py-10">
		<!-- Learning Profile Results - Centered Container -->
		<div class="max-w-4xl px-4 mx-auto mb-12">
			<div class="p-8 bg-white shadow-lg rounded-xl">
				<h2 class="mb-6 text-2xl font-semibold text-center text-gray-900">
					Zwischenergebnis Lerntypen Test
				</h2>

				<!-- Chart Container -->
				<div class="flex justify-center mb-8">
					<LearningRadarChart
						:scores="modalityScores"
						:max-score="100"
						:canvas-size="500"
					/>
				</div>

				<!-- Dominant Learning Style -->
				<div class="p-6 mb-6 rounded-lg bg-gradient-to-r from-blue-50 to-indigo-50">
					<h3 class="flex items-center gap-2 mb-3 text-lg font-semibold text-gray-900">
						<AppIcon name="lucide:star" size="lg" class="text-yellow-500" />
						{{ personalizationProfile.childName }} ist ein {{ dominantLearningStyle.title }}-Lerner!
					</h3>
					<p class="leading-relaxed text-gray-700">
						{{ dominantLearningStyle.description }}
					</p>
				</div>

				<!-- Learning Style Badges -->
				<div class="grid grid-cols-2 gap-4 mb-8 md:grid-cols-4">
					<ScoreBadge
						v-for="entry in modalityScoreEntries"
						:key="entry.key"
						:score="entry.score"
						:title="getSectionShortTitle(entry.key)"
						:icon="getSectionIcon(entry.key)"
						class="transition-all duration-200 hover:scale-105"
					/>
				</div>

				<!-- Quick Tips -->
				<div class="p-6 rounded-lg bg-green-50">
					<h3 class="flex items-center gap-2 mb-4 text-lg font-semibold text-green-900">
						<AppIcon name="lucide:lightbulb" size="md" class="text-green-600" />
						3 Sofort-Tipps für {{ personalizationProfile.childName }}
					</h3>
					<div class="space-y-3">
						<div v-for="(tip, index) in quickTips" :key="index" class="flex items-start">
							<span
								class="flex items-center justify-center flex-shrink-0 w-6 h-6 mr-3 text-sm font-medium text-green-800 bg-green-200 rounded-full"
							>
								{{ index + 1 }}
							</span>
							<p class="text-green-800">
								{{ tip }}
							</p>
						</div>
					</div>
				</div>
			</div>
		</div>

		<!-- Premium CTA Section - Full Width -->
		<MarketingCTA
			headline="Weitermachen und Komplettanalyse einer Lerntherapeutin bekommen"
			description="Der zweite Teil liefert Ihnen die notwendige Grundlage, um die Herausforderungen (z.B. ADHS-Tendenzen oder Hochsensibilität) Ihres Kindes besser zu verstehen und sofort umsetzbare Empfehlungen für den Alltag zu erhalten."
			:value-props="premiumValueProps"
			:primary-c-t-a="{ text: 'Jetzt Teil 2 starten!' }"
			:trust-indicators="trustIndicators"
			variant="default"
			primary-button-variant="green"
			make-value-props-clickable
			@primary-cta-click="handleContinue"
		/>
	</div>
</template>

<script setup lang="ts">
import { computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { type SectionId } from '../data/learningTest';
import ScoreBadge from '@monorepo/shared-ui/components/ScoreBadge.vue';
import LearningRadarChart from '@/components/learning/LearningRadarChart.vue';
import AppIcon from '@/components/icons/AppIcon.vue';
import { usePersonalization } from '@/composables/usePersonalization';
import { useLearningScores } from '@/composables/useLearningScores';
import {
	getSectionIcon,
	getSectionShortTitle,
	getDominantStyleDescription,
	getQuickTips,
} from '@/utils/sectionHelpers';
import MarketingCTA from '@/components/marketing/MarketingCTA.vue';
import type { ValueProp } from '@/components/marketing/types';
import { useBreakPageExperiment } from '@/composables/useBreakPageExperiment';
import { useConversionTracking } from '@/composables/usePageTracking';

const router = useRouter();
const { personalizationProfile } = usePersonalization();
const { modalityScores, getDominantStyle } = useLearningScores();
const { trackPhase2ContinueGoal } = useBreakPageExperiment();
const { trackFunnelStep } = useConversionTracking();

const premiumValueProps: ValueProp[] = [
	{
		title: 'Vollständige Analyse',
		description: 'Alle 9 Lernpräferenzen',
		icon: 'lucide:brain',
	},
	{
		title: 'Personalisierte Strategien',
		description: 'Speziell für Ihr Kind',
		icon: 'lucide:target',
	},
	{
		title: 'Langfristige Erfolge',
		description: 'Lebenslange Lernfähigkeit',
		icon: 'lucide:trending-up',
	},
];

const trustIndicators = [
	'Wissenschaftlich fundiert',
	'Von Pädagogen empfohlen',
	'Über 15.000 Familien vertrauen uns',
	'Lebenslange Unterstützung',
];

// Properly typed score entries for template - avoids type assertions
const modalityScoreEntries = computed(() => {
	return Object.entries(modalityScores.value).map(([key, score]) => ({
		key: key as SectionId,
		score,
	}));
});

// Get dominant learning style using shared helper
const dominantLearningStyle = computed(() => {
	const dominant = getDominantStyle();
	const childName = personalizationProfile.value.childName;

	return {
		title: dominant.title,
		description: getDominantStyleDescription(dominant.type, childName),
	};
});

// Quick tips based on dominant learning style using shared helper
const quickTips = computed(() => {
	const dominant = getDominantStyle();
	return getQuickTips(dominant.type);
});

// Event handlers
function handleContinue(): void {
	// Track conversion goal reached (phase 2 started from break page)
	trackPhase2ContinueGoal();
	trackFunnelStep('wizard_phase2_started', { phase1Time: undefined });
	// Navigate to Phase 2 of the wizard
	// Note: Phase 2 start tracking happens in WizardContainer when it mounts
	router.push('/wizard/phase2');
}

onMounted(() => {
	// Track that user actually saw the break page (break variant)
	trackFunnelStep('break_page_shown', { experimentId: 'break_page_v1', variantId: 'break' });
});
</script>
